// The pure data-overlay evaluator (TGCLOUD-1670).
//
// (spec, facts, personIndex, asOf) -> matched people with per-match detail.
//
// Client-side on purpose. `resolveCrossEntityFilters` cannot AND two predicates
// over the SAME related record (it resolves each filter independently and
// intersects the id sets), and "everyone with an appointment that is not closed
// AND whose date has passed" is exactly that. It also cannot express absence:
// it only ever intersects positive id sets, with no anti-join. Both are one line
// each here, and one family's facts are already in memory.
//
// Cost: O(members x notes). The whole clinical analyzer already runs
// synchronously in a useMemo (usePedigreeAnalysis.js:51-73) and does far more.

import {
  RESOLVED_APPOINTMENT_STATUSES,
  getRelevantDate,
  dueBand
} from 'src/util/recordDueDates';
import {
  classifyCarrierStatus,
  geneOf
} from 'src/components/pedigree-chart/pedigree-tool/analysis/carrierStatusVocabulary';
import { applyOperator } from './operators';
import {
  readDate,
  resolveDateValue,
  daysSince,
  daysUntil,
  compareDays,
  isDateField,
  ageAt,
  stampAsOf
} from './dates';

/** Constants a preset may name by reference, so the vocabulary lives in one place. */
const NAMED_CONSTANTS = {
  RESOLVED_APPOINTMENT_STATUSES
};

// ---------------------------------------------------------------------------
// Materialising a spec: preset + params -> a concrete, comparable criteria object
// ---------------------------------------------------------------------------

function substitute(value, params) {
  if (value == null || typeof value !== 'object') return value;
  if (Array.isArray(value)) return value.map((v) => substitute(v, params));

  if (typeof value.ref === 'string') {
    const constant = NAMED_CONSTANTS[value.ref];
    if (!constant) throw new Error(`Unknown named constant: ${value.ref}`);
    const list = Array.isArray(constant) ? [...constant] : constant;
    return Array.isArray(list) && Array.isArray(value.except)
      ? list.filter((v) => !value.except.includes(v))
      : list;
  }

  if (typeof value.param === 'string') {
    const raw = params?.[value.param];
    return value.negate ? -Number(raw || 0) : raw;
  }

  if (value.rel) {
    const offset = substitute(value.offsetDays, params);
    return {
      rel: value.rel,
      ...(offset == null ? {} : { offsetDays: Number(offset) })
    };
  }

  const out = {};
  for (const [k, v] of Object.entries(value)) out[k] = substitute(v, params);
  return out;
}

/**
 * Canonical ordering so two specs built by different routes compare equal.
 * The manual popover and the Copilot both go through here, and a golden test
 * asserts they produce deep-equal objects for every preset.
 */
export function normaliseCriteria(spec) {
  const where = [...(spec.where || [])].sort((a, b) =>
    `${a.field}|${a.op}`.localeCompare(`${b.field}|${b.op}`)
  );
  return {
    source: spec.source,
    where,
    reduce: spec.reduce || { kind: 'any' },
    then: spec.then || null,
    and: (spec.and || []).map(normaliseCriteria),
    andNot: (spec.andNot || []).map(normaliseCriteria)
  };
}

export function defaultParams(preset) {
  const out = {};
  for (const d of preset?.paramDefs || []) out[d.key] = d.default;
  return out;
}

/** The ONLY function that turns a preset plus parameters into a criteria object. */
export function materialiseCriteria(preset, params = {}) {
  if (!preset) throw new Error('materialiseCriteria: no preset');
  const merged = { ...defaultParams(preset), ...params };
  return normaliseCriteria(substitute(preset.spec, merged));
}

// ---------------------------------------------------------------------------
// Field resolution
// ---------------------------------------------------------------------------

const DERIVED_DATE_RE = /^\$(daysSince|daysUntil)\(([A-Za-z0-9_.]+)\)$/;

function dottedGet(obj, path) {
  if (!obj) return undefined;
  const segments = path.split('.');
  let cursor = obj;
  for (let i = 0; i < segments.length; i += 1) {
    if (cursor == null) return undefined;
    if (Array.isArray(cursor)) {
      // Any-element semantics, as Mongo: collect the remaining path per element.
      const rest = segments.slice(i).join('.');
      return cursor
        .map((el) => dottedGet(el, rest))
        .filter((v) => v !== undefined);
    }
    cursor = cursor[segments[i]];
  }
  return cursor;
}

/** Resolve a field on ONE record (a note, a member, or an array element). */
function resolveField(field, record, ctx) {
  const derived = DERIVED_DATE_RE.exec(field);
  if (derived) {
    const [, kind, dateField] = derived;
    const d = readDate(dateField, dottedGet(record, dateField));
    return kind === 'daysSince'
      ? daysSince(d, ctx.asOf)
      : daysUntil(d, ctx.asOf);
  }

  switch (field) {
    case '$relevantDate':
      return readDate('reminderDate', getRelevantDate(record));
    case '$overdueDays': {
      const d = readDate('reminderDate', getRelevantDate(record));
      return daysSince(d, ctx.asOf);
    }
    case '$deceased':
      return Boolean(record?.dod || String(record?.isAlive) === 'false');
    case '$age':
      return ageAt(record?.dob, ctx.asOf);
    case '$address.count':
      return ctx.address?.count ?? 0;
    case '$address.hasPrimary':
      return ctx.address?.hasPrimary ?? false;
    case '$address.hasCorrespondence':
      return ctx.address?.hasCorrespondence ?? false;
    case '$gene':
      return geneOf(record);
    case '$carrierResult':
      return classifyCarrierStatus(record?.carrierStatus);
    default:
      return dottedGet(record, field);
  }
}

function testPredicate(pred, record, ctx) {
  const actual = resolveField(pred.field, record, ctx);

  // Date fields compare as calendar days, never as instants or strings.
  const dateField =
    isDateField(pred.field) ||
    (pred.value && typeof pred.value === 'object' && pred.value.rel);
  if (dateField && !DERIVED_DATE_RE.test(pred.field)) {
    if (pred.op === 'exists' || pred.op === 'notExists') {
      return applyOperator(pred.op, actual, null);
    }
    const left = readDate(pred.field, actual);
    const right = resolveDateValue(pred.value, ctx.asOf);
    const diff = compareDays(left, right);
    if (diff == null) return pred.op === 'notEquals' || pred.op === 'notIn';
    switch (pred.op) {
      case 'equals':
        return diff === 0;
      case 'notEquals':
        return diff !== 0;
      case 'lessThan':
        return diff < 0;
      case 'lessThanOrEqual':
        return diff <= 0;
      case 'greaterThan':
        return diff > 0;
      case 'greaterThanOrEqual':
        return diff >= 0;
      default:
        return false;
    }
  }

  return applyOperator(pred.op, actual, pred.value);
}

const matchesAll = (where, record, ctx) =>
  (where || []).every((p) => testPredicate(p, record, ctx));

// ---------------------------------------------------------------------------
// Reducers
// ---------------------------------------------------------------------------

function reduceRecords(reduce, records, ctx) {
  const kind = reduce?.kind || 'any';
  if (kind === 'any')
    return { matched: records.length > 0, record: records[0] };
  if (kind === 'none') return { matched: records.length === 0, record: null };
  if (kind === 'count')
    return { matched: true, value: records.length, record: records[0] };

  if (!records.length) return { matched: false, record: null };

  if (kind === 'latest' || kind === 'earliest') {
    const sorted = [...records].sort((a, b) => {
      const da = readDate(reduce.field, dottedGet(a, reduce.field));
      const db = readDate(reduce.field, dottedGet(b, reduce.field));
      if (!da && !db) return 0;
      if (!da) return 1; // undated sorts last in both directions
      if (!db) return -1;
      return kind === 'latest' ? db - da : da - db;
    });
    return { matched: true, record: sorted[0] };
  }

  if (kind === 'min' || kind === 'max') {
    let best = null;
    let bestValue = null;
    for (const r of records) {
      const v = resolveField(reduce.field, r, ctx);
      if (!Number.isFinite(v)) continue;
      if (
        bestValue == null ||
        (kind === 'min' ? v < bestValue : v > bestValue)
      ) {
        bestValue = v;
        best = r;
      }
    }
    return best
      ? { matched: true, record: best, value: bestValue }
      : { matched: false, record: null };
  }

  return { matched: false, record: null };
}

// ---------------------------------------------------------------------------
// One person against one spec
// ---------------------------------------------------------------------------

function recordsFor(spec, person, ctx) {
  const { source } = spec;
  if (source.type === 'member') return [person.member || {}];
  if (source.type === 'memberArray') {
    const arr = dottedGet(person.member || {}, source.path);
    return Array.isArray(arr) ? arr : [];
  }
  return ctx.notesFor(person.personKey, source.noteType);
}

function evaluateSpec(spec, person, ctx) {
  const records = recordsFor(spec, person, ctx).filter((r) =>
    matchesAll(spec.where, r, ctx)
  );
  const reduced = reduceRecords(spec.reduce, records, ctx);

  let matched = reduced.matched;
  if (matched && spec.then) {
    matched = applyOperator(spec.then.op, reduced.value, spec.then.value);
  }
  if (matched) {
    matched = (spec.and || []).every(
      (s) => evaluateSpec(s, person, ctx).matched
    );
  }
  if (matched) {
    matched = (spec.andNot || []).every(
      (s) => !evaluateSpec(s, person, ctx).matched
    );
  }
  return { matched, record: reduced.record, value: reduced.value };
}

/**
 * A person is INDETERMINATE, not a gap, when the overlay asks "no record of type
 * X" and that person has private records of type X the endpoint would not read.
 * Silently treating them as a gap is the one failure a gap-spotting tool must
 * not have. `private` exists only on Clinical Note and Process, so only those
 * two types can ever produce this state.
 */
function isIndeterminate(spec, person, ctx) {
  const excluded = ctx.excludedByPerson?.[person.personKey];
  if (!excluded) return false;
  const negativeTypes = [];
  const walk = (s, negated) => {
    if (s.source?.type === 'note') {
      if (negated || s.reduce?.kind === 'none')
        negativeTypes.push(s.source.noteType);
    }
    (s.and || []).forEach((sub) => walk(sub, negated));
    (s.andNot || []).forEach((sub) => walk(sub, true));
  };
  walk(spec, false);
  return negativeTypes.some((t) => (excluded[t] || 0) > 0);
}

// ---------------------------------------------------------------------------
// Presentation
// ---------------------------------------------------------------------------

function pillText(preset, outcome, ctx) {
  const pill = preset?.pill;
  if (!pill) return '';
  if (typeof pill === 'string') {
    const count = outcome.value;
    return preset.countPill && Number.isFinite(count) && count > 1
      ? `${count} ${pill}`
      : pill;
  }
  if (pill.map) {
    const raw = outcome.record
      ? resolveField(pill.field, outcome.record, ctx)
      : null;
    return pill.map[raw] || pill.fallback || '';
  }
  const v = outcome.record
    ? resolveField(pill.field, outcome.record, ctx)
    : null;
  if (!Number.isFinite(v)) return preset.title.slice(0, 6);
  return `${Math.abs(Math.round(v))}${pill.suffix || ''}`;
}

function weightFor(preset, outcome, ctx) {
  if (preset?.weight !== 'dueBands' || !preset.weightField || !outcome.record) {
    return null;
  }
  const days = resolveField(preset.weightField, outcome.record, ctx);
  return dueBand(days)?.key || null;
}

/**
 * Tooltip text. Statuses, dates and counts only: never a name, never free text,
 * never a date of birth. The projection already withholds the rest, and this is
 * the second line of that defence.
 */
function summaryFor(preset, outcome, ctx) {
  const parts = [preset.title];
  const r = outcome.record;
  if (r) {
    const dateField = (preset.spec?.reduce?.field || '')
      .replace(DERIVED_DATE_RE, '$2')
      .replace(/^\$(daysSince|daysUntil)\(|\)$/g, '');
    const d = dateField ? readDate(dateField, dottedGet(r, dateField)) : null;
    if (d) parts.push(ctx.fmtDate ? ctx.fmtDate(d) : d.toDateString());
    const status =
      r.appointmentStatus ||
      r.contactStatus ||
      r.referralStatus ||
      r.response ||
      r.processStatus ||
      r.testResult;
    if (status) parts.push(String(status));
    if (Number.isFinite(outcome.value) && outcome.value > 1) {
      parts.push(`${outcome.value} records`);
    }
  }
  return parts.filter(Boolean).join(' — ').replace(/ — /g, ', ');
}

// ---------------------------------------------------------------------------
// Entry point
// ---------------------------------------------------------------------------

/**
 * @param {object} args
 * @param {object} args.preset      catalogue entry (for presentation)
 * @param {object} args.criteria    materialised spec
 * @param {object} args.facts       the overlay-facts payload (may be null)
 * @param {object} args.personIndex from resolvePersons
 * @param {Date}   [args.asOf]      evaluation day, defaults to now
 * @param {Function} [args.fmtDate] display formatter for tooltips
 */
export function evaluateDataOverlay({
  preset,
  criteria,
  facts,
  personIndex,
  asOf = new Date(),
  fmtDate
}) {
  const members = personIndex?.list || [];
  const factMembers = facts?.members || {};

  const ctx = {
    asOf,
    fmtDate,
    excludedByPerson: facts?.excludedByPerson || {},
    address: null,
    notesFor: (personKey, noteType) =>
      factMembers[personKey]?.notes?.[noteType] || []
  };

  const matches = [];
  let hiddenCount = 0;
  let indeterminateCount = 0;

  for (const person of members) {
    const personCtx = {
      ...ctx,
      address: factMembers[person.personKey]?.address || null
    };

    if (isIndeterminate(criteria, person, personCtx)) {
      indeterminateCount += 1;
      continue;
    }

    const outcome = evaluateSpec(criteria, person, personCtx);
    if (!outcome.matched) continue;

    if (!person.onCanvas) hiddenCount += 1;

    matches.push({
      personKey: person.personKey,
      uid: person.uid,
      pids: person.pids,
      onCanvas: person.onCanvas,
      label: person.label,
      detail: {
        summary: summaryFor(preset, outcome, personCtx),
        pill: pillText(preset, outcome, personCtx),
        weight: weightFor(preset, outcome, personCtx)
      }
    });
  }

  return {
    presetId: preset?.id || null,
    title: preset?.title || '',
    asOf: stampAsOf(asOf),
    matches,
    count: matches.length,
    hiddenCount,
    indeterminateCount,
    unresolvedCount: facts?.unresolved?.length || 0,
    rosterSize: members.length,
    rosterOnCanvas: members.filter((m) => m.onCanvas).length,
    unavailable: facts?.unavailable || null
  };
}

/**
 * The two per-slot choices the criteria popover offers, applied to a finished
 * result rather than threaded through matching, because neither changes who
 * MATCHES: they change who is counted and who is drawn.
 *
 * Both default on, and both defaults are the safe reading for a tool whose job
 * is spotting gaps. Counting hidden members means a person a saved view has
 * taken off the chart still appears in the total and in the hidden tally, so the
 * clinician is told the family is incomplete rather than shown a chart that
 * looks complete. Marking every rendering means a person drawn twice (a proxy
 * pair) carries the mark on both, so neither rendering reads as a gap that is
 * not there.
 */
export function applySlotOptions(
  results,
  { countHidden = true, markEveryRendering = true } = {}
) {
  if (!results) return results;
  let matches = results.matches;
  let hiddenCount = results.hiddenCount;
  let rosterSize = results.rosterSize;

  if (!countHidden) {
    matches = matches.filter((m) => m.onCanvas);
    hiddenCount = 0;
    rosterSize = results.rosterOnCanvas;
  }
  if (!markEveryRendering) {
    matches = matches.map((m) =>
      m.pids.length > 1 ? { ...m, pids: m.pids.slice(0, 1) } : m
    );
  }
  if (matches === results.matches) return results;

  return {
    ...results,
    matches,
    count: matches.length,
    hiddenCount,
    rosterSize
  };
}
